import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { Fade } from '@mui/material';
import { Helmet } from 'react-helmet';
import { ToastSuccess, ToastError, ToastWarning, ToastInfo } from 'components/shared/toasts';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Authenticated from 'components/auth';
import ErrorBoundary from 'components/shared/error-boundary/ErrorBoundary';
import FullScreenLoading from 'components/shared/loading/FullScreenLoading';
import LockedSectionBlock from 'components/shared/blocks/LockedSectionBlock';
import IncompleteProfileBlock from 'components/shared/blocks/IncompleteProfileBlock';
import BeamContextProvider from 'context/BeamContextProvider';
import EchoContextProvider from 'context/EchoContextProvider';
import LayoutContextProvider from 'context/LayoutContextProvider';
import SidebarDrawerContextProvider from 'context/SidebarDrawerContextProvider';
import MobileSidebarDrawer from 'components/shared/drawers/MobileSidebarDrawer';
import RoleTour from 'components/shared/tour/RoleTour';
import { publicRoutes, privateRoutes } from 'router/routes';
import RouteSync from 'router/RouteSync';
import Notifier from 'components/shared/Notifier';
import usePaymentListener from 'hooks/payments/usePaymentListener';
import useNewsListener from 'hooks/notifications/useNewsListener';
import usePlanRequestListener from 'hooks/notifications/usePlanRequestListener';
import usePlanRequestAdminListener from 'hooks/notifications/usePlanRequestAdminListener';
import { roles } from 'common/constants/options';
import routes from 'common/routes';

/**
 * RouterContent - Inner component that has access to EchoContext
 * Initializes global listeners for payment and news events
 */
function RouterContent() {
  const { t } = useTranslation(['labels', 'buttons']);
  usePaymentListener();
  useNewsListener();
  usePlanRequestListener();
  usePlanRequestAdminListener();

  const userRole = useSelector((state) => state.auth.user?.role);
  const membershipStatus = useSelector((state) => state.auth.user?.membership_status);
  const profileComplete = useSelector((state) => state.auth.user?.profile_complete);
  const planId = useSelector((state) => state.auth.user?.plan_id?.id);
  const platform = useSelector((state) => state.auth.user?.platform);
  const isOrgManager = useSelector((state) => state.auth.user?.is_org_manager) === true;

  return (
    <LayoutContextProvider>
      {/* El drawer de navegación móvil vive AQUÍ, por encima de <Routes>, y no dentro del layout.
          Cada ruta construye su propio `element` con su `<Layout>`, así que al navegar React
          Router desmontaba el layout entero y con él este drawer. Si la pantalla destino ya estaba
          precargada (render síncrono), el desmontaje pillaba al Modal a media animación de cierre:
          MUI no llegaba a soltar el bloqueo de scroll ni el velo, y el panel se quedaba congelado
          encima del contenido hasta recargar la página. Montado aquí sobrevive a la navegación, la
          animación siempre termina y la limpieza se ejecuta. */}
      <SidebarDrawerContextProvider>
        <Notifier />
        {userRole && <MobileSidebarDrawer />}
        {/* El tour también sale de la ruta, y por el mismo motivo que el drawer: vivía en el
            layout, así que se reiniciaba en CADA navegación. Cada reinicio volvía a abrir el panel
            para medir sus anclas, de modo que un tutorial sin completar reabría el menú una y otra
            vez. Montado aquí arranca una sola vez y decide por la ruta, no por el remontaje. */}
        {userRole && <RoleTour role={userRole} />}
        <Routes>
        {privateRoutes.map(({ key, path, component: Component, layout: Layout, allowedRoles, requiresMembership, requiresOrgManager, allowedPlatforms }) => {
          // El gestor de empresa (panel Prometeo) es un CLIENT con flag, no un rol
          // aparte: los tres guards de cliente le aplicarían y lo dejarían encerrado en el
          // bloqueo de membresía o de perfil, cuando él entra a administrar su empresa y no
          // a entrenar. Por eso queda exceptuado explícitamente de los tres.
          const isLockedForClient =
            requiresMembership &&
            userRole === roles.client &&
            !isOrgManager &&
            membershipStatus === 'INACTIVE' &&
            !planId;

          const isProfileIncomplete =
            userRole === roles.client &&
            !isOrgManager &&
            profileComplete === false &&
            path !== routes.clientProfile.path;

          // Gating de canal: rutas con allowedPlatforms solo para clientes de ese canal
          // (p. ej. /play/* solo para otrix_play). Otros roles no se ven afectados.
          const isWrongPlatform =
            allowedPlatforms &&
            userRole === roles.client &&
            !isOrgManager &&
            !allowedPlatforms.includes(platform);

          // Espejo del guard anterior: las rutas /company solo para quien tiene el flag.
          const isNotOrgManager = requiresOrgManager && !isOrgManager;

          // El inicio del gestor de empresa es su panel, no el dashboard de cliente: el login,
          // el Gate y la verificación de correo mandan a todos los CLIENT a /dashboard, así que
          // el desvío se hace aquí, en el único punto por el que pasan todos.
          const isOrgManagerOnClientHome = isOrgManager && path === routes.clientDashboard.path;

          let routeContent;
          if (isNotOrgManager) {
            // Antes era un `<Navigate>` mudo al dashboard de cliente: a quien le retiraban el
            // permiso de contacto de empresa la app simplemente lo escupía de /company sin
            // decirle por qué, y parecía un error. Ahora se le explica.
            routeContent = (
              <LockedSectionBlock
                message={t('orgAccessRevoked', {
                  ns: 'labels',
                  defaultValue:
                    'Fuiste removido de esta organización. Por favor consulta con un administrador de OTRIX Wellness Club.'
                })}
                ctaLabel={t('goHome', { ns: 'buttons', defaultValue: 'Ir al inicio' })}
                ctaTo={routes.clientDashboard.path}
              />
            );
          } else if (isOrgManagerOnClientHome) {
            routeContent = <Navigate to={routes.orgClientDashboard.path} replace />;
          } else if (isProfileIncomplete) {
            routeContent = <IncompleteProfileBlock />;
          } else if (isLockedForClient) {
            routeContent = <LockedSectionBlock />;
          } else if (isWrongPlatform) {
            routeContent = <Navigate to={routes.clientDashboard.path} replace />;
          } else {
            routeContent = <Component />;
          }

          return (
            <Route
              key={key}
              path={path}
              element={
                <Authenticated allowedRoles={allowedRoles}>
                  <Layout>
                    {/* Aísla el crash de una pantalla: si un componente de ruta lanza,
                        mostramos el error inline (con el layout/sidebar intactos) en vez
                        de desmontar toda la app y dejar la ventana en blanco. La key por
                        `path` remonta el boundary al navegar, así un error no queda pegado. */}
                    <ErrorBoundary key={path} name={key}>
                      {/* Frontera de SUSPENSIÓN local: cualquier carga en frío (un chunk lazy, un
                          namespace de i18n que alguien olvide precargar) se contiene AQUÍ, en el
                          contenido de la ruta. Antes subía hasta el único Suspense de App.js, que
                          envuelve el Router entero: React ocultaba TODO el árbol, incluido el
                          drawer móvil en plena animación de cierre, y su aviso de salida se perdía
                          para siempre. Resultado reproducido con navegador (2026-08-17): panel
                          zombie con aria-hidden="true", backdrop comiéndose los toques y solo el
                          reload lo quitaba. Con esta frontera, una suspensión jamás vuelve a tocar
                          al drawer, la barra inferior ni los portales. */}
                      <Suspense fallback={<FullScreenLoading />}>
                        {routeContent}
                      </Suspense>
                    </ErrorBoundary>
                  </Layout>
                </Authenticated>
              }
            />
          );
        })}

        {publicRoutes.map(({ key, path, component: Component, layout: Layout, boundaryKey }) => (
          <Route
            key={key}
            path={path}
            element={
              <Layout>
                {/* boundaryKey compartido (opt-in): las rutas del AuthPortal ('/', '/login',
                    '/register') conservan el MISMO subtree al navegar entre ellas para que la
                    transición deslice sin remount. Las demás siguen remontando por path. */}
                <ErrorBoundary key={boundaryKey || path} name={key}>
                  {/* Misma frontera local que en las rutas privadas (ver arriba). */}
                  <Suspense fallback={<FullScreenLoading />}>
                    <Component />
                  </Suspense>
                </ErrorBoundary>
              </Layout>
            }
          />
        ))}
        </Routes>
      </SidebarDrawerContextProvider>
    </LayoutContextProvider>
  );
}

function Router() {
  return (
    <BrowserRouter>
      {/* Toasts premium: nacen desde arriba (referencia MySchoolUAE/react-hot-toast). La
          animación visible vive en OtxToast (keyframe); Fade solo cubre la salida para no
          duplicar movimiento con la transición de notistack. */}
      <SnackbarProvider
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        Components={{
          success: ToastSuccess,
          error: ToastError,
          warning: ToastWarning,
          info: ToastInfo
        }}
        TransitionComponent={Fade}
        transitionDuration={{ enter: 50, exit: 350 }}
        maxSnack={3}
        autoHideDuration={4000}>
        <Helmet>
          <title>Otrix Wellness Club</title>
        </Helmet>
        <RouteSync />
        <BeamContextProvider>
          <EchoContextProvider>
            <RouterContent />
          </EchoContextProvider>
        </BeamContextProvider>
      </SnackbarProvider>
    </BrowserRouter>
  );
}

export default Router;
